import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { accessLocalStorage, exportSelectAllEndpointsResult, exportDeleteEndpoints } from '../../../services';
import { scopeByTenant } from '../../../services/tenantScope';
import { parseEndpointContext } from '../../../services/outboundGroupContexts';
import { CONFIG_ROLES } from '../../../constants/roles';
import { hasFeature } from '../../../services/permissions';

import PbxContext from '../../../context/PbxContext';
import Loading from '../../../components/Loading/LoadingModule';
import './EndpointsList.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';

const TABLE_COLUMNS_COUNT = 14;

const CONTEXT_LABELS = {
  'interno': 'Chamadas Internas',
  'from-extensions': 'Chamadas Internas',
  'local-fixo': 'Chamadas Local Fixo',
  'local-celular': 'Chamadas Local Celular',
  'ddd-fixo': 'Chamadas DDD Fixo',
  'ddd-celular': 'Chamadas DDD Celular',
  'internacional': 'Internacional',
};

const formatContextLabel = (context, tenant) => {
  if (!context) return '';
  const { privilege, group } = parseEndpointContext(context, tenant);
  const label = CONTEXT_LABELS[privilege] || context;
  return group ? `${label} · ${group}` : label;
};

const normalizeEndpoint = (element) => {
  const { pass, transportName, contextName, languageName, busy, captureGroup, maxContacts } = element;
  element['password'] = pass;
  element['transport'] = transportName;
  element['context'] = contextName;
  element['language'] = languageName;
  element['state'] = busy;
  element['pickupGroup'] = captureGroup;
  element['maxContacts'] = maxContacts || 1;

  delete element.pass;
  delete element.transportName;
  delete element.contextName;
  delete element.languageName;
  delete element.captureGroup;

  return element;
};

function EndpointsList() {
  const getItensStateGlobal = useContext(PbxContext);
  const { setItemsSelectedToEdit } = getItensStateGlobal;

  const [loading, setLoading] = useState(true);
  const [phones, setPhones] = useState(() => []);
  const [endpointsSelected, setEndpointsSelected] = useState([]);
  const [disable, setDisable] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [listError, setListError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteWarning, setDeleteWarning] = useState(null);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const history = useHistory();

  const filteredPhones = phones.filter((phone) => {
    const searchText = filterText.toLowerCase();
    return (
      phone.endpoint.toString().includes(searchText) ||
      (phone.password && phone.password.toLowerCase().includes(searchText)) ||
      (phone.callerid && phone.callerid.toLowerCase().includes(searchText)) ||
      (phone.transport && phone.transport.toLowerCase().includes(searchText)) ||
      (phone.context && phone.context.toLowerCase().includes(searchText)) ||
      (phone.language && phone.language.toLowerCase().includes(searchText)) ||
      (phone.codec && phone.codec.toLowerCase().includes(searchText))
    );
  });

  const fetchEndpoints = async (accountcode) => {
    const result = await exportSelectAllEndpointsResult();

    if (!result.success) {
      setListError(result.error);
      setPhones([]);
      setEndpointsSelected([]);
      setDisable(true);
      setLoading(false);
      return false;
    }

    const endpointsList = scopeByTenant(result.data, (endpoint) => endpoint.tenant, accountcode);
    const elements = endpointsList
      .map(normalizeEndpoint)
      .filter((element) => !isNaN(Number(element.endpoint)));

    setListError(null);
    setPhones(elements);
    setLoading(false);

    return true;
  };

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser || !dataUser.user) return history.push('/');

    const { role, idtenant } = dataUser.user;
    if (!CONFIG_ROLES.includes(role) && !hasFeature('config.ramais', 'view')) return history.push('/home');

    const endpointsFetched = await fetchEndpoints(idtenant);
    return endpointsFetched;
  }, []);

  const clearSelection = () => {
    const selectAllCheckbox = document.getElementById('select-all');
    if (selectAllCheckbox) selectAllCheckbox.checked = false;

    phones.forEach((phone) => {
      const checkbox = document.getElementById(phone.endpoint);
      if (checkbox) checkbox.checked = false;
    });

    setEndpointsSelected([]);
    setDisable(true);
  };

  const handleChangeSelectedEndpoints = (element) => {
    setEndpointsSelected((prevState) => {
      const onlineState = prevState.includes(element) ? [...prevState.filter((e) => e !== element)] : [...prevState, element];
      onlineState.length > 0 ? setDisable(false) : setDisable(true)
      return onlineState;
    });
  };

  const formatCleanupWarnings = (warnings) => {
    if (!Array.isArray(warnings) || warnings.length === 0) return null;
    return warnings
      .map((warning) => {
        const concern = warning && warning.concern ? warning.concern : 'limpeza';
        const message = warning && warning.message ? warning.message : 'falha desconhecida';
        return `${concern}: ${message}`;
      })
      .join('; ');
  };

  const handleClickDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    setDeleteWarning(null);

    const result = await exportDeleteEndpoints(endpointsSelected);

    setDeleting(false);
    setConfirmDeleteVisible(false);

    if (!result.success) {
      setDeleteError(result.error);
      return;
    }

    const warningsText = formatCleanupWarnings(result.data && result.data.warnings);
    if (warningsText) {
      setDeleteWarning(
        `Ramais excluídos, mas parte da limpeza auxiliar falhou (${warningsText}). A lista será atualizada.`
      );
    }

    clearSelection();
    return validateUserLogged();
  };

  const handleClickUpdate = async () => {
    setItemsSelectedToEdit(endpointsSelected);
    return history.push('/config/ramal/id');
  };

  const handleRetryLoad = () => {
    setLoading(true);
    return validateUserLogged();
  };

  const handleChangeSelectAll = () => {
    const inputSelectAll = document.getElementById('select-all');
    const checked = !!(inputSelectAll && inputSelectAll.checked);

    phones.forEach((phone) => {
      const checkbox = document.getElementById(phone.endpoint);
      if (checkbox) checkbox.checked = false;
    });

    if (!checked) {
      setDisable(true);
      setEndpointsSelected([]);
      return;
    }

    filteredPhones.forEach((phone) => {
      const checkbox = document.getElementById(phone.endpoint);
      if (checkbox) checkbox.checked = true;
    });

    const selected = filteredPhones.map((phone) => phone.endpoint);
    setDisable(selected.length === 0);
    setEndpointsSelected(selected);
  };

  const emptyTableMessage = () => {
    if (listError) return 'Lista indisponível: os ramais não puderam ser carregados.';
    if (phones.length === 0) return 'Nenhum ramal cadastrado para este cliente.';
    return 'Nenhum ramal corresponde ao filtro informado.';
  };

  useEffect(() => {
    let cancel = false;

    if (cancel) return;
    validateUserLogged();

    return () => {
      cancel = true
    };
  }, [validateUserLogged]);

  return (
    <div>
      {
        loading ?
          <Loading />
        :
        <div className="endpoints-list-container">
          <div className="endpoints-header">
            <div className="endpoints-title-wrapper">
              <div className="endpoints-title-left">
                <svg className="endpoints-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <div>
                  <h1 className="endpoints-title">Ramais</h1>
                  <p className="endpoints-subtitle">Gerencie os ramais do sistema</p>
                </div>
              </div>
              <div className="endpoints-actions">
                {hasFeature('config.ramais', 'create') && (
                  <Link to="/config/ramal/novo" className="btn-endpoints btn-new-endpoint">
                    <FontAwesomeIcon icon={ faPlus } className="btn-icon" />
                    Novo Ramal
                  </Link>
                )}
                {hasFeature('config.ramais', 'edit') && (
                  <button
                    className="btn-endpoints btn-edit-endpoint"
                    disabled={disable}
                    onClick={() => handleClickUpdate()}
                  >
                    <FontAwesomeIcon icon={ faPen } className="btn-icon" />
                    Editar
                  </button>
                )}
                {hasFeature('config.ramais', 'delete') && !confirmDeleteVisible && (
                  <button
                    className="btn-endpoints btn-delete-endpoint"
                    disabled={disable}
                    onClick={() => {
                      setDeleteError(null);
                      setDeleteWarning(null);
                      setConfirmDeleteVisible(true);
                    }}
                  >
                    <FontAwesomeIcon icon={ faTrash } className="btn-icon" />
                    Excluir
                  </button>
                )}
              </div>
            </div>
          </div>

          {(listError || deleteError || deleteWarning || confirmDeleteVisible) && (
            <div className="alerts-section">
              {listError && (
                <div className="alert-message alert-danger">
                  <div className="alert-header">
                    <span>Atenção!</span>
                  </div>
                  <div className="alert-body">
                    { listError } A lista abaixo não reflete os ramais cadastrados.
                  </div>
                  <div className="alert-actions">
                    <button className="btn-alert btn-alert-cancel" onClick={() => handleRetryLoad()}>
                      Tentar novamente
                    </button>
                  </div>
                </div>
              )}

              {deleteError && (
                <div className="alert-message alert-danger">
                  <div className="alert-header">
                    <span>Atenção!</span>
                    <button className="alert-close" aria-label="fechar" onClick={() => setDeleteError(null)}>×</button>
                  </div>
                  <div className="alert-body">
                    Os ramais selecionados não foram excluídos. { deleteError }
                  </div>
                </div>
              )}

              {deleteWarning && (
                <div className="alert-message alert-warning">
                  <div className="alert-header">
                    <span>Atenção!</span>
                    <button className="alert-close" aria-label="fechar" onClick={() => setDeleteWarning(null)}>×</button>
                  </div>
                  <div className="alert-body">
                    { deleteWarning }
                  </div>
                </div>
              )}

              {confirmDeleteVisible && (
                <div className="alert-message alert-danger">
                  <div className="alert-header">
                    <span>Atenção!</span>
                    <button className="alert-close" aria-label="fechar" onClick={() => setConfirmDeleteVisible(false)}>×</button>
                  </div>
                  <div className="alert-body">
                    { endpointsSelected.length === 1
                      ? 'Tem certeza que quer excluir 1 ramal selecionado?'
                      : `Tem certeza que quer excluir ${endpointsSelected.length} ramais selecionados?` }
                  </div>
                  <div className="alert-actions">
                    <button className="btn-alert btn-alert-confirm" disabled={deleting} onClick={() => handleClickDelete()}>
                      <FontAwesomeIcon icon={ faTrash } />
                      { deleting ? 'Excluindo...' : 'Confirmar' }
                    </button>
                    <button className="btn-alert btn-alert-cancel" disabled={deleting} onClick={() => setConfirmDeleteVisible(false)}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="filter-section">
            <div className="filter-input-wrapper">
              <svg className="filter-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                className="filter-input"
                placeholder="Filtrar por ramal, senha, nome, protocolo, contexto, codec..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
              {filterText && (
                <button className="filter-clear" onClick={() => setFilterText('')}>
                  ×
                </button>
              )}
            </div>
            <div className="filter-results">
              { listError ? 'Lista indisponível' : `Exibindo ${filteredPhones.length} de ${phones.length} ramal(is)` }
            </div>
          </div>

          <div className="endpoints-table-wrapper">
            <table className="endpoints-table">
              <thead className="endpoints-table-header">
                  <tr>
                  <th className="checkbox-cell">
                    <input id="select-all" type="checkbox" onClick={() => handleChangeSelectAll()}/>
                  </th>
                  <th>Ramal</th>
                  <th>Senha</th>
                  <th>Nome</th>
                  <th>Protocolo</th>
                  <th>Previlégios</th>
                  <th>DTMF</th>
                  <th>Linguagem</th>
                  <th>Codec</th>
                  <th>NAT</th>
                  <th>Ocupado</th>
                  <th>Max. Disp.</th>
                  <th>Grupo Atendedor</th>
                  <th>Grupo de Captura</th>
                  </tr>
              </thead>
              <tbody>
              {
                filteredPhones.length === 0 ?
                  <tr>
                    <td colSpan={ TABLE_COLUMNS_COUNT }>{ emptyTableMessage() }</td>
                  </tr>
                :
                filteredPhones.map((phone) => {
                  const { endpoint, password, callerid, transport, context, language, codec, dtmf, state, maxContacts, callGroup, pickupGroup, nat, tenant } = phone;
                  return (
                    <tr key={ endpoint }>
                      <td className="checkbox-cell"><input id={ endpoint } type="checkbox" value={endpoint} onClick={ (e) => handleChangeSelectedEndpoints(e.target.value)}/></td>
                      <td>{endpoint}</td>
                      <td>{password}</td>
                      <td>{callerid}</td>
                      <td>{transport || '—'}</td>
                      <td>{formatContextLabel(context, tenant)}</td>
                      <td>{dtmf}</td>
                      <td>{language}</td>
                      <td>{codec}</td>
                      <td>{nat}</td>
                      <td>{state}</td>
                      <td>{maxContacts || 1}</td>
                      <td>{callGroup}</td>
                      <td>{pickupGroup}</td>
                    </tr>)
                })
              }
              </tbody>
            </table>
          </div>
        </div>
      }
    </div>
  );
}

export default EndpointsList;
